import { useState, useEffect, useRef } from "react";
import _ from "lodash";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, CircularProgress } from "@mui/material";

import CustomLayout from "views/CustomLayout";
import ErrorRetry from "components/ErrorRetry";
import axiosInstance from "configs/axiosConfig";
import { APIRouteConstants } from "constants/routeConstants";
import { getLastApiSuccess } from "utils/apiActivity";
import { getCachedOrFetch } from "utils/apiCache";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatCurrencyNew } from "utils/helper";
import { useStore } from "utils/OfflineContext";
import { useAuth } from "oidc-react";

import "./makwaLanding.scss";

const bgImage = require("../../assets/images/Webbie_default_banner.jpg");

// Shown when the store hasn't configured any banner of its own.
const DEFAULT_BANNER = {
  url: "https://docketell-file.s3.amazonaws.com/website_banners/Webbie_default_banner.jpg",
  main_title: "Shop Your Favorites",
  sub_title: "Explore our latest collections and exclusive offers.",
};

// ---------- Hero ----------
export const MakwaHero = ({ websiteInfo, pageTitle, pageSubtitle }) => {
  const navigate = useNavigate();
  const [imgErrors, setImgErrors] = useState({});
  // websiteInfo.banner is an array of { url, main_title, sub_title, bg_color,
  // shape_color }. Older data may send a bare string (or single object), so
  // normalize every entry to that object shape.
  const rawBanners =
    Array.isArray(websiteInfo?.banner) && websiteInfo.banner.length
      ? websiteInfo.banner
      : websiteInfo?.banner
        ? [websiteInfo.banner]
        : [];
  const banners = (rawBanners.length ? rawBanners : [DEFAULT_BANNER]).map(
    (item) => (typeof item === "string" ? { url: item } : item || {}),
  );

  const fallbackTitle = websiteInfo?.store_name || "Shop the latest essentials";
  const fallbackSubtitle =
    websiteInfo?.description ||
    "Discover our curated collection of must-have products. Shop now and redefine your everyday.";

  return (
    <Swiper
      key={banners.length}
      className="mk-hero-carousel"
      modules={[Autoplay, Pagination]}
      slidesPerView={1}
      loop={banners.length > 1}
      autoplay={banners.length > 1 ? { delay: 3500, disableOnInteraction: false } : false}
      pagination={{ clickable: true }}
    >
      {banners.map((banner, index) => {
        const title = banner.main_title || pageTitle || fallbackTitle;
        const subtitle = banner.sub_title || pageSubtitle || fallbackSubtitle;
        const imgSrc = imgErrors[index] ? undefined : banner.url;
        return (
          <SwiperSlide key={index}>
            <section
              className="mk-hero"
              aria-label={title}
              style={banner.bg_color ? { background: banner.bg_color } : undefined}
            >
              <div className="mk-hero__copy">
                <h1 style={banner.header_text_color ? { color: banner.header_text_color } : undefined}>
                  {title}
                </h1>
                <p style={banner.sub_header_text_color ? { color: banner.sub_header_text_color } : undefined}>
                  {subtitle}
                </p>
                <button
                  type="button"
                  className="mk-btn"
                  style={{
                    ...(banner.cta_bg_color ? { background: banner.cta_bg_color } : undefined),
                    ...(banner.cta_text_color ? { color: banner.cta_text_color } : undefined),
                  }}
                  onClick={() => navigate("/products")}
                >
                  Explore More
                </button>
              </div>
              <div className="mk-hero__visual" aria-hidden="true">
                <div
                  className="mk-hero__circle"
                  style={banner.shape_color ? { background: banner.shape_color } : undefined}
                />
                {imgSrc && (
                  <div className="mk-hero__media">
                    <img
                      src={imgSrc}
                      alt={title}
                      loading="eager"
                      onError={() =>
                        setImgErrors((prev) => ({ ...prev, [index]: true }))
                      }
                    />
                  </div>
                )}
              </div>
            </section>
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
};

// ---------- Filters ----------
const COLLECTION_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Top Collection", value: "top" },
  { label: "Featured", value: "featured" },
];

const PRICE_OPTIONS = [
  { label: "Default", value: "default" },
  { label: "Price: Low to High", value: "low" },
  { label: "Price: High to Low", value: "high" },
  { label: "On Sale", value: "sale" },
];

const SORT_OPTIONS = [
  { label: "Default", value: "default" },
  { label: " A - Z", value: "az" },
  { label: "Z - A", value: "za" },
];

const FilterDropdown = ({ label, value, options, onSelect, align }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="mk-dropdown" ref={ref}>
      <button
        type="button"
        className="mk-pill"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {label} <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </button>
      {open && (
        <ul
          className={`mk-dropdown__menu${
            align === "right" ? " mk-dropdown__menu--right" : ""
          }`}
          role="menu"
        >
          {options.map((opt) => (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="menuitem"
                className={`mk-dropdown__item${
                  value === opt.value ? " mk-dropdown__item--active" : ""
                }`}
                onClick={() => {
                  setOpen(false);
                  onSelect(opt.value);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MakwaFilters = ({
  showCollection,
  collectionFilter,
  onSelectCollection,
  categoryFilter,
  onSelectCategory,
  categoryOptions,
  priceFilter,
  onSelectPrice,
  sortFilter,
  onSelectSort,
}) => {
  return (
    <section className="mk-filters" aria-label="Product filters">
      <div className="mk-filters__group">
        {showCollection && (
          <FilterDropdown
            label="Collection"
            value={collectionFilter}
            options={COLLECTION_OPTIONS}
            onSelect={onSelectCollection}
          />
        )}
        <FilterDropdown
          label="Category"
          value={categoryFilter}
          options={categoryOptions}
          onSelect={onSelectCategory}
        />
        <FilterDropdown
          label="Price"
          value={priceFilter}
          options={PRICE_OPTIONS}
          onSelect={onSelectPrice}
        />
      </div>
      <FilterDropdown
        label="Sort By"
        value={sortFilter}
        options={SORT_OPTIONS}
        onSelect={onSelectSort}
        align="right"
      />
    </section>
  );
};

// ---------- Product card ----------
const MakwaProductCard = ({ product, currencySymbol }) => {
  const navigate = useNavigate();
  const variant = product?.variants && product?.variants[0];
  const image = variant?.images?.[0]?.image || variant?.images?.[0]?.image_url;

  const price = Number(variant?.price || 0);
  const discount = Number(variant?.discount_price || 0);
  const hasDiscount = discount > 0 && discount < price;
  const discountPercent =
    hasDiscount && price > 0 ? Math.round(((price - discount) / price) * 100) : 0;

  const totalStock = (product?.variants || []).reduce(
    (sum, v) => sum + Number(v?.stock_count || 0),
    0,
  );
  const isOutOfStock = totalStock === 0;

  const open = () => navigate(`/product/${variant?.id}`);

  return (
    <article className="mk-product" onClick={open}>
      {hasDiscount && <span className="mk-badge">SALE</span>}
      <div className="mk-product__image">
        <img
          src={image}
          alt={product?.name}
          loading="lazy"
          className={isOutOfStock ? "mk-product__image--oos" : undefined}
        />
        {isOutOfStock && (
          <span className="mk-oos-badge">Out of Stock</span>
        )}
      </div>
      <div className="mk-product__body">
        <h3>{_.truncate(_.trim(product?.name), { length: 48 })}</h3>
        <div className="mk-product__footer">
          <p className="mk-price">
            <span>{formatCurrencyNew(hasDiscount ? discount : price, currencySymbol)}</span>
            {hasDiscount && <s>{formatCurrencyNew(price, currencySymbol)}</s>}
          </p>
          <button
            type="button"
            className="mk-product__cart"
            aria-label={`View ${product?.name}`}
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 17 }} />
          </button>
        </div>
      </div>
    </article>
  );
};

// ---------- Product grid section ----------
const MakwaGrid = ({ id, title, products, currencySymbol, showLoadMore }) => {
  const navigate = useNavigate();
  if (!products || products.length === 0) return null;

  return (
    <section id={id} className="mk-products" aria-labelledby={`${id}-title`}>
      <div className="mk-section__head">
        <h2 id={`${id}-title`}>{title}</h2>
      </div>
      <div className="mk-grid">
        {products.map((product, index) => (
          <MakwaProductCard
            key={`${product.id}-${index}`}
            product={product}
            currencySymbol={currencySymbol}
          />
        ))}
      </div>
      {showLoadMore && (
        <button type="button" className="mk-loadmore" onClick={() => navigate("/products")}>
          View More Products
        </button>
      )}
    </section>
  );
};

const Landing = () => {
  const webDetails = useSelector((state) => state.webDetails);
  const { websiteInfo } = webDetails;
  const loggedInUser = useSelector((state) => state.userDetails?.user);
  const { setStoreOff } = useStore();
  const [topCollection, setTopCollection] = useState([]);
  const [featureCollection, setFeatureCollection] = useState([]);
  const [productList, setProductList] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState("default");
  const [sortFilter, setSortFilter] = useState("default");
  const auth = useAuth();

  const normalizeProduct = (item) => {
    return {
      ...item,
      id: item?.id || item?.id,
      name: item?.product_name || item?.name,
      product_image:
        (item?.images && item?.images[0]?.image) || item?.product_image,
    };
  };

  function updateMetaDescription(newDescription) {
    const metaDescriptionTag = document.querySelector(
      "meta[name='description']",
    );
    if (metaDescriptionTag) {
      metaDescriptionTag.setAttribute("content", newDescription);
    } else {
      const newMetaTag = document.createElement("meta");
      newMetaTag.setAttribute("name", "description");
      newMetaTag.setAttribute("content", newDescription);
      document.head.appendChild(newMetaTag);
    }
  }

  useEffect(() => {
    if (auth?.userData)
      localStorage.setItem("token-expiry", auth?.userData?.expires_at);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFavicon(newFavicon) {
    const faviconLink = document.getElementById("favicon");
    if (faviconLink) {
      faviconLink.setAttribute("href", newFavicon);
    } else {
      const newFaviconLink = document.createElement("link");
      newFaviconLink.setAttribute("rel", "icon");
      newFaviconLink.setAttribute("href", newFavicon);
      document.head.appendChild(newFaviconLink);
    }
  }

  function updateAppleTouchIcon(newAppleTouchIcon) {
    const appleTouchIconLink = document.getElementById("apple-touch-icon");
    if (appleTouchIconLink) {
      appleTouchIconLink.setAttribute("href", newAppleTouchIcon);
    } else {
      const newAppleTouchIconLink = document.createElement("link");
      newAppleTouchIconLink.setAttribute("rel", "apple-touch-icon");
      newAppleTouchIconLink.setAttribute("href", newAppleTouchIcon);
      document.head.appendChild(newAppleTouchIconLink);
    }
  }

  function updateManifest(newManifest) {
    const manifestLink = document.getElementById("manifest");
    if (manifestLink) {
      manifestLink.setAttribute("href", newManifest);
    } else {
      const newManifestLink = document.createElement("link");
      newManifestLink.setAttribute("rel", "manifest");
      newManifestLink.setAttribute("href", newManifest);
      document.head.appendChild(newManifestLink);
    }
  }

  // CustomLayout (which always wraps this page) already fetches the current
  // user on mount and puts it in Redux — mirror that into localStorage
  // instead of hitting AUTH.ME a second time here.
  useEffect(() => {
    if (!_.isEmpty(loggedInUser)) {
      localStorage.setItem("u_user_data", JSON.stringify(loggedInUser));
    }
  }, [loggedInUser]);

  useEffect(() => {
    localStorage.removeItem("vouchercode");
    const userData = localStorage.getItem("u_user_data");
    if (webDetails && webDetails?.websiteInfo?.store_name === "The Vet Store") {
      document.title = "The Vet Store";
      updateMetaDescription("The Vet Store");
      updateFavicon("/favicon1.ico");
      updateAppleTouchIcon("/vetstore-512x512.png");
      updateManifest("/manifestvet.json");
    } else if (webDetails && webDetails?.websiteInfo?.store_name === "Kwa Fries") {
      document.title = "Kwa Flava. Kwa Experience. Where bold flavour meets unforgettable events.";
      updateMetaDescription("Kwa Flava. Kwa Experience. Where bold flavour meets unforgettable events.");
      updateFavicon("/favicon2.ico");
      updateAppleTouchIcon("/favicon2.ico");
      updateManifest("/manifestvet.json");
    } else if (webDetails && webDetails?.websiteInfo?.store_name) {
      document.title = `Digital ordering solution | ${webDetails?.websiteInfo?.store_name}`;
      updateMetaDescription(
        `Digital ordering solution | ${webDetails?.websiteInfo?.store_name}`,
      );
      updateFavicon("/favicon.ico");
      updateAppleTouchIcon(
        webDetails?.websiteInfo?.banner
          ? webDetails?.websiteInfo?.banner
          : "/logo192.png",
      );
      updateManifest("/manifest.json");
    }
    setStoreOff(websiteInfo?.whatsapp_status);

    if (
      window.location.hostname.includes("alora") &&
      window.dataLayer &&
      userData
    ) {
      window.dataLayer.push({
        event: "email_tracking",
        userEmail: JSON.parse(userData)?.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webDetails]);

  // Each fetcher returns the normalized array on success, or null on failure —
  // null (not just an empty array) is how the orchestrator below tells "the
  // request failed" apart from "this collection is legitimately empty".
  const getTopCollection = async () => {
    try {
      const normalized = await getCachedOrFetch("top-collection-products", async () => {
        let res = await axiosInstance.get(
          APIRouteConstants.DASHBOARD.TOP_COLLECTION,
        );
        let { data } = res;
        const filteredDisabledCollection = (data || []).filter(
          (item) => item?.variants[0]?.is_disabled === false,
        );
        return Array.isArray(filteredDisabledCollection)
          ? filteredDisabledCollection.map(normalizeProduct)
          : [];
      });
      setTopCollection(normalized);
      return normalized;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const getFeatureCollection = async () => {
    try {
      const normalized = await getCachedOrFetch("featured-products", async () => {
        let res = await axiosInstance.get(
          APIRouteConstants.DASHBOARD.FEATURE_COLLECTION,
        );
        let { data } = res;
        const filteredDisabledCollection = (data || []).filter(
          (item) => item?.variants[0]?.is_disabled === false,
        );
        return Array.isArray(filteredDisabledCollection)
          ? filteredDisabledCollection.map(normalizeProduct)
          : [];
      });
      setFeatureCollection(normalized);
      return normalized;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // Fallback source: when neither the top nor featured collection has any
  // products, fill the grid from the general product list instead.
  const getProductList = async () => {
    try {
      let res = await axiosInstance.get(APIRouteConstants.DASHBOARD.ALL_PRODUCT);
      let { data } = res;
      if (data?.results) {
        const filteredDisabledCollection = data.results.filter(
          (item) => item?.variants?.[0]?.is_disabled === false,
        );
        const normalized = filteredDisabledCollection.map(normalizeProduct);
        setProductList(normalized);
        return normalized;
      }
      return [];
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  // Products section owns its own loading/error state, independent of the
  // page shell — a failed fetch here shows an inline retry instead of
  // blanking the whole page.
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(false);

  const loadProducts = async () => {
    setProductsLoading(true);
    setProductsError(false);

    const [top, feat] = await Promise.all([
      getTopCollection(),
      getFeatureCollection(),
    ]);
    const topItems = top || [];
    const featItems = feat || [];
    let anyFailed = top === null || feat === null;

    let fallbackItems = [];
    if (topItems.length === 0 && featItems.length === 0) {
      const fallback = await getProductList();
      if (fallback === null) anyFailed = true;
      else fallbackItems = fallback;
    }

    const hasAnyProducts =
      topItems.length > 0 || featItems.length > 0 || fallbackItems.length > 0;
    if (!hasAnyProducts && anyFailed) {
      setProductsError(true);
    }
    setProductsLoading(false);
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currencySymbol = websiteInfo?.currency_symbol;

  // Default "All" merges both collections (deduped by id); the Collection
  // dropdown narrows the grid to just the top or featured list. When both
  // collections are empty, fall back to the general product list.
  const hasCollections =
    featureCollection.length > 0 || topCollection.length > 0;
  const mergedProducts = hasCollections
    ? _.uniqBy(
        [...featureCollection, ...topCollection],
        (item) => item?.variants?.[0]?.id || item?.id,
      )
    : productList;
  const baseProducts =
    collectionFilter === "top"
      ? topCollection
      : collectionFilter === "featured"
        ? featureCollection
        : mergedProducts;

  // Category options are the distinct categories present in the current
  // collection, so the Category dropdown only offers what's actually shown.
  const categoryOptions = [
    { label: "All", value: "all" },
    ..._.uniq(baseProducts.map((p) => p?.category_name).filter(Boolean)).map(
      (name) => ({ label: name, value: name }),
    ),
  ];

  const categoryProducts =
    categoryFilter === "all"
      ? baseProducts
      : baseProducts.filter((p) => p?.category_name === categoryFilter);

  // Effective price honours an active discount, matching the product card.
  const getEffectivePrice = (item) => {
    const v = item?.variants?.[0];
    const price = Number(v?.price || 0);
    const discount = Number(v?.discount_price || 0);
    return discount > 0 && discount < price ? discount : price;
  };
  const isOnSale = (item) => {
    const v = item?.variants?.[0];
    const price = Number(v?.price || 0);
    const discount = Number(v?.discount_price || 0);
    return discount > 0 && discount < price;
  };

  let displayedProducts = categoryProducts;
  if (priceFilter === "sale") {
    displayedProducts = categoryProducts.filter(isOnSale);
  } else if (priceFilter === "low") {
    displayedProducts = [...categoryProducts].sort(
      (a, b) => getEffectivePrice(a) - getEffectivePrice(b),
    );
  } else if (priceFilter === "high") {
    displayedProducts = [...categoryProducts].sort(
      (a, b) => getEffectivePrice(b) - getEffectivePrice(a),
    );
  }

  // Name sort is applied last, on top of any price ordering/filtering.
  if (sortFilter === "az" || sortFilter === "za") {
    displayedProducts = [...displayedProducts].sort((a, b) => {
      const cmp = (a?.name || "").localeCompare(b?.name || "");
      return sortFilter === "az" ? cmp : -cmp;
    });
  }

  const gridTitle =
    collectionFilter === "top"
      ? "Top Collection"
      : collectionFilter === "featured"
        ? "Featured"
        : "Shop your favourites";

  // Switching collection resets the category filter so a stale category
  // (absent from the new collection) can't blank out the grid.
  const handleSelectCollection = (value) => {
    setCollectionFilter(value);
    setCategoryFilter("all");
  };

  return (
    <CustomLayout>
      <div className="makwa-landing">
        <MakwaHero websiteInfo={websiteInfo} />
        <MakwaFilters
          showCollection={hasCollections}
          collectionFilter={collectionFilter}
          onSelectCollection={handleSelectCollection}
          categoryFilter={categoryFilter}
          onSelectCategory={setCategoryFilter}
          categoryOptions={categoryOptions}
          priceFilter={priceFilter}
          onSelectPrice={setPriceFilter}
          sortFilter={sortFilter}
          onSelectSort={setSortFilter}
        />
        {productsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : productsError ? (
          <ErrorRetry
            onRetry={loadProducts}
            lastUpdated={getLastApiSuccess(APIRouteConstants.DASHBOARD.ALL_PRODUCT)}
          />
        ) : (
          <MakwaGrid
            id="featured"
            title={gridTitle}
            products={displayedProducts}
            currencySymbol={currencySymbol}
            showLoadMore
          />
        )}
      </div>
    </CustomLayout>
  );
};

export default Landing;
